import React, { useState, useEffect, useRef } from 'react';
import './cv-upload.css';
import { useAppContext } from '../../hooks/useAppContext';
import { getRoleAnalyses } from '../../utils/api';

const CVUploadTab = () => {
  const { jobs, loadJobs } = useAppContext();
  const [activeJob, setActiveJob] = useState("");
  const [activeFilter, setActiveFilter] = useState("pass");
  const [locationFilter, setLocationFilter] = useState("");   // ← NEW: location filter
  const [openIdx, setOpenIdx] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);

  const [stats, setStats] = useState({ total: 0, pass: 0, fail: 0 });
  const [syncing, setSyncing] = useState(false);
  const [lastSyncMins, setLastSyncMins] = useState(0);

  const [driveModalOpen, setDriveModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadRole, setUploadRole] = useState("");
  const [scoreModal, setScoreModal] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [toastMsg, setToastMsg] = useState("");
  const toastTimeout = useRef(null);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const fetchCandidates = async (roleId) => {
    setIsLoadingCandidates(true);
    try {
      if (roleId === '__all__') {
        const allResults = await Promise.all(
          jobs.map(async job => {
            const r = await getRoleAnalyses(job.roleId || job.id);
            return { job, data: r.success ? r.data || [] : [] };
          })
        );
        const mapped = allResults.flatMap(({ job, data }) =>
          data.map(c => {
            const rawScore = c.overallScore || c.cv_score || c.score || 0;
            const scoreNum = typeof rawScore === 'string'
              ? parseFloat(rawScore.replace('/10', ''))
              : parseFloat(rawScore);
            const expText = c.experienceYears != null ? `${c.experienceYears} years` : (c.experience || 'N/A');
            const eduText = typeof c.education === 'string' ? c.education : (c.education || 'N/A');
            return {
              name: c.name || c.candidate_name || 'Unknown',
              email: c.email || '',
              phone: c.phone || 'N/A',
              location: c.location || 'N/A',
              education: eduText,
              experience: expText,
              score: isNaN(scoreNum) ? 0 : scoreNum,
              cv_score: isNaN(scoreNum) ? 0 : scoreNum,
              status: c.status || '',
              cv_file_path: c.cv_file_path || '',
              scoring_details: c.scoring_details || null,
              jobTitle: job.title || 'Unknown Role'
            };
          })
        );
        setCandidates(mapped);
        const pass = mapped.filter(x => x.score >= 7).length;
        setStats({ total: mapped.length, pass, fail: mapped.length - pass });
      } else {
        const result = await getRoleAnalyses(roleId);
        if (result.success) {
          const mapped = (result.data || []).map(c => {
            const rawScore = c.overallScore || c.cv_score || c.score || 0;
            const scoreNum = typeof rawScore === 'string'
              ? parseFloat(rawScore.replace('/10', ''))
              : parseFloat(rawScore);
            const expText = c.experienceYears != null ? `${c.experienceYears} years` : (c.experience || 'N/A');
            const eduText = typeof c.education === 'string' ? c.education : (c.education || 'N/A');
            return {
              name: c.name || c.candidate_name || 'Unknown',
              email: c.email || '',
              phone: c.phone || 'N/A',
              location: c.location || 'N/A',
              education: eduText,
              experience: expText,
              score: isNaN(scoreNum) ? 0 : scoreNum,
              cv_score: isNaN(scoreNum) ? 0 : scoreNum,
              status: c.status || '',
              cv_file_path: c.cv_file_path || '',
              scoring_details: c.scoring_details || null,
              jobTitle: null
            };
          });
          setCandidates(mapped);
          const pass = mapped.filter(x => x.score >= 7).length;
          setStats({ total: mapped.length, pass, fail: mapped.length - pass });
        }
      }
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const quietFetchCandidates = async (roleId) => {
    try {
      const result = await getRoleAnalyses(roleId);
      if (result.success) {
        const mapped = (result.data || []).map(c => {
          const rawScore = c.overallScore || c.cv_score || c.score || 0;
          const scoreNum = typeof rawScore === 'string'
            ? parseFloat(rawScore.replace('/10', ''))
            : parseFloat(rawScore);
          const expText = c.experienceYears != null ? `${c.experienceYears} years` : (c.experience || 'N/A');
          const eduText = typeof c.education === 'string' ? c.education : (c.education || 'N/A');
          return {
            name: c.name || c.candidate_name || 'Unknown',
            email: c.email || '',
            phone: c.phone || 'N/A',
            location: c.location || 'N/A',
            education: eduText,
            experience: expText,
            score: isNaN(scoreNum) ? 0 : scoreNum,
            cv_score: isNaN(scoreNum) ? 0 : scoreNum,
            status: c.status || '',
            cv_file_path: c.cv_file_path || '',
            scoring_details: c.scoring_details || null
          };
        });
        setCandidates(mapped);
        const pass = mapped.filter(x => x.score >= 7 || x.cv_score >= 7).length;
        setStats({ total: mapped.length, pass, fail: mapped.length - pass });
      }
    } catch (err) {
      console.error('Quiet fetch failed:', err);
    }
  };

  useEffect(() => {
    if (!activeJob) {
      setCandidates([]);
      setStats({ total: 0, pass: 0, fail: 0 });
      setLocationFilter("");
      return;
    }
    if (activeJob === '__all__') {
      fetchCandidates('__all__');
      return;
    }
    const selectedJob = jobs.find(j => (j.roleId || j.id) === activeJob);
    const roleId = selectedJob?.roleId || activeJob;
    fetchCandidates(roleId);
  }, [activeJob, jobs]);

  useEffect(() => {
    let interval;
    const hasProcessing = candidates.some(c => c.status === 'processing' || c.status === 'new');
    if (hasProcessing && activeJob) {
      const selectedJob = jobs.find(j => (j.roleId || j.id) === activeJob);
      const roleId = selectedJob?.roleId || activeJob;
      interval = setInterval(() => { quietFetchCandidates(roleId); }, 3000);
    }
    return () => clearInterval(interval);
  }, [candidates, activeJob, jobs]);

  useEffect(() => {
    const timer = setInterval(() => { setLastSyncMins(m => m + 1); }, 60000);
    return () => clearInterval(timer);
  }, []);

  // ── Derive unique locations from current candidates list ──────────────────
  const uniqueLocations = React.useMemo(() => {
    const locs = candidates
      .map(c => (c.location || '').trim())
      .filter(l => l && l !== 'N/A');
    return [...new Set(locs)].sort();
  }, [candidates]);

  const openJobs = jobs.filter(j => j.status === 'Open');

  const showToast = (msg) => {
    setToastMsg(msg);
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(() => setToastMsg(""), 3200);
  };

  const getFiltered = () => {
    if (!activeJob) return [];
    let list = [...candidates];
    // Score filter
    if (activeFilter === "pass") list = list.filter(x => x.score >= 7);
    if (activeFilter === "high") list = list.filter(x => x.score >= 8.5);
    // Location filter — case-insensitive exact match on selected location
    if (locationFilter) {
      list = list.filter(x =>
        (x.location || '').trim().toLowerCase() === locationFilter.toLowerCase()
      );
    }
    return list;
  };

  const isAllJobs = activeJob === '__all__';
  const filteredCvs = getFiltered();

  const scoreClass = (s) => s >= 8.5 ? "score-high" : s >= 7 ? "score-mid" : "score-low";
  const barColor  = (s) => s >= 8.5 ? "#00b894" : s >= 7 ? "#f59e0b" : "#e74c3c";

  const renderStatusBadge = (s) => {
    if (s === "new")  return <span className="cv-badge badge-new"><span className="badge-dot"></span>New</span>;
    if (s === "sent") return <span className="cv-badge badge-sent"><span className="badge-dot"></span>Sent</span>;
    if (s === "error") return <span className="cv-badge badge-fail" style={{ background: '#fde8e8', color: '#e74c3c' }}><span className="badge-dot" style={{ background: '#e74c3c' }}></span>AI Error</span>;
    return <span className="cv-badge badge-reviewed">Reviewed</span>;
  };

  const handleToggleDrawer = (index) => { setOpenIdx(openIdx === index ? null : index); };

  const handleSync = async () => {
    if (!activeJob) { showToast("Please select a job position first"); return; }
    setSyncing(true);
    try {
      const selectedJob = jobs.find(j => (j.roleId || j.id) === activeJob);
      const roleId = selectedJob?.roleId || activeJob;
      await fetchCandidates(roleId);
      setLastSyncMins(0);
      showToast("🔄 Synced with latest data");
    } catch (err) {
      showToast("❌ Failed to sync data");
    } finally {
      setSyncing(false);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadRole || uploadFiles.length === 0) return;
    setSyncing(true);
    try {
      const tenantId = process.env.REACT_APP_TENANT_ID || "tenant123";
      const apiUrl   = process.env.REACT_APP_API_URL   || "https://irpapi.selficai.com/api/v1";

      let successCount = 0, duplicateCount = 0, failCount = 0, lastDuplicateMsg = "";

      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append("cv_file",   file);
        formData.append("tenant_id", tenantId);
        formData.append("role_id",   uploadRole);
        formData.append("username",  "admin");
        try {
          const res = await fetch(`${apiUrl}/cv/upload`, { method: "POST", body: formData });
          if (res.ok)              { successCount++; }
          else if (res.status === 409) { duplicateCount++; lastDuplicateMsg = `${file.name} has already been uploaded to this role.`; }
          else                     { failCount++; }
        } catch (err) { console.error("Upload error:", err); failCount++; }
      }

      if (duplicateCount > 0 && successCount === 0)      showToast(`⚠️ Skipped: ${lastDuplicateMsg}`);
      else if (duplicateCount > 0 && successCount > 0)   showToast(`✅ ${successCount} uploaded. ⚠️ ${duplicateCount} were already uploaded.`);
      else if (failCount > 0)                             showToast(`✅ ${successCount} processed, ❌ ${failCount} failed.`);
      else                                                showToast(`✅ Upload complete: ${successCount} processed successfully.`);

      setUploadModalOpen(false);
      setUploadFiles([]);
      setUploadRole("");

      if (successCount > 0) {
        const selectedJob = jobs.find(j => j.id === activeJob);
        if (selectedJob && (selectedJob.roleId === uploadRole || selectedJob.id === uploadRole)) {
          fetchCandidates(uploadRole);
        }
      }
    } catch (err) {
      showToast("❌ Upload failed");
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="cv-upload-wrapper">
      {/* STAT CARDS */}
      <div className="stat-row">
        <div className="stat-card">
          <div className="stat-top">
            <span className="stat-label">Total Processed</span>
            <div className="stat-icon-wrap" style={{ background: '#ede9fc' }}>📥</div>
          </div>
          <div className="stat-value purple">{stats.total}</div>
          <div className="stat-footer">All Time</div>
        </div>
        <div className="stat-card">
          <div className="stat-top">
            <span className="stat-label">Passed Screening</span>
            <div className="stat-icon-wrap" style={{ background: '#e0faf4' }}>✅</div>
          </div>
          <div className="stat-value green">{stats.pass}</div>
          <div className="stat-footer">score ≥ 7.0</div>
        </div>
        <div className="stat-card">
          <div className="stat-top">
            <span className="stat-label">Filtered Out</span>
            <div className="stat-icon-wrap" style={{ background: '#fde8e8' }}>❌</div>
          </div>
          <div className="stat-value red">{stats.fail}</div>
          <div className="stat-footer">score &lt; 7.0</div>
        </div>
      </div>

      {/* MAIN CARD */}
      <div className="cv-card">
        {/* SYNC INFO BAR */}
        <div className="sync-info-bar">
          <div className="sync-live">
            <div className="sync-dot"></div>
            Live data sync enabled
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="sync-time">
              Last sync: {lastSyncMins === 0 ? 'just now' : `${lastSyncMins} min ago`}
            </span>
            <button className="btn btn-purple btn-sm" disabled={syncing} onClick={handleSync}>
              {syncing ? (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ animation: 'syncPulse 0.7s linear infinite' }}>
                  <path d="M8 2a6 6 0 1 1-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M13.5 2.5A6.5 6.5 0 0 0 2 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M2.5 13.5A6.5 6.5 0 0 0 14 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M2 5.5V2.5H5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 10.5V13.5H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {syncing ? ' Syncing...' : ' Sync Now'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setDriveModalOpen(true)}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Configure Drive
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setUploadModalOpen(true)}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M8 11V3M8 3L5 6M8 3L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 13H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Upload Manually
            </button>
          </div>
        </div>

        {/* TOOLBAR */}
        <div className="cv-toolbar">
          <div className="toolbar-left" style={{ flexWrap: 'wrap', gap: '8px' }}>
            {/* Job selector */}
            <select value={activeJob} onChange={(e) => { setActiveJob(e.target.value); setOpenIdx(null); setLocationFilter(""); }}>
              <option value="">— Select Job Position —</option>
              <option value="__all__">🌐 All Positions</option>
              {openJobs.length === 0 ? (
                <option value="" disabled>No open positions available</option>
              ) : (
                openJobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))
              )}
            </select>

            {/* Score filter chips */}
            <button className={`cv-chip ${activeFilter === 'pass' ? 'active' : ''}`} onClick={() => { setActiveFilter('pass'); setOpenIdx(null); }}>✅ Passed (≥7.0)</button>
            <button className={`cv-chip ${activeFilter === 'all'  ? 'active' : ''}`} onClick={() => { setActiveFilter('all');  setOpenIdx(null); }}>All CVs</button>
            <button className={`cv-chip ${activeFilter === 'high' ? 'active' : ''}`} onClick={() => { setActiveFilter('high'); setOpenIdx(null); }}>⭐ High (≥8.5)</button>

            {/* ── Location filter dropdown ── */}
            {activeJob && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                  <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.5 4.5 8.5 4.5 8.5s4.5-5 4.5-8.5c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.4" />
                </svg>
                <select
                  className="location-filter-select"
                  value={locationFilter}
                  onChange={(e) => { setLocationFilter(e.target.value); setOpenIdx(null); }}
                >
                  <option value="">All Locations</option>
                  {uniqueLocations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
                {locationFilter && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setLocationFilter("")}
                    title="Clear location filter"
                    style={{ padding: '4px 8px', fontSize: '11px' }}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
            )}
          </div>

          <span className="result-count">
            {activeJob ? `${filteredCvs.length} candidate${filteredCvs.length !== 1 ? 's' : ''}${locationFilter ? ` in ${locationFilter}` : ''}` : ''}
          </span>
        </div>

        {/* TABLE */}
        <div className="table-wrap">
          {!activeJob ? (
            <div className="cv-empty-state">
              <div className="cv-empty-icon">📋</div>
              <div className="cv-empty-title">No job position selected</div>
              <div className="cv-empty-sub">Choose a job position above to view its screened CVs.</div>
            </div>
          ) : isLoadingCandidates ? (
            <div className="cv-empty-state">
              <div className="cv-empty-icon">⏳</div>
              <div className="cv-empty-title">Loading candidates...</div>
            </div>
          ) : filteredCvs.length === 0 ? (
            <div className="cv-empty-state">
              <div className="cv-empty-icon">🔍</div>
              <div className="cv-empty-title">No CVs match this filter</div>
              <div className="cv-empty-sub">
                {locationFilter
                  ? `No candidates found in "${locationFilter}". Try a different location or clear the filter.`
                  : 'Try "All CVs" to see every uploaded CV for this job.'}
              </div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Candidate</th>
                  {isAllJobs && <th>Job Position</th>}
                  <th>Location</th>
                  <th>Experience</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th style={{ width: '36px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredCvs.map((c, i) => {
                  const pct = (c.score / 10) * 100;
                  const isExpanded = openIdx === i;
                  return (
                    <React.Fragment key={`${c.email}-${i}`}>
                      <tr className={isExpanded ? 'expanded' : ''} onClick={() => handleToggleDrawer(i)}>
                        <td>
                          <div className="cand-name">{c.name}</div>
                          <div className="cand-email">{c.email}</div>
                        </td>
                        {isAllJobs && (
                          <td style={{ fontSize: '12px', fontWeight: 600, color: '#667eea' }}>
                            {c.jobTitle || '—'}
                          </td>
                        )}
                        <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{c.location}</td>
                        <td style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '180px' }}>{c.exp}</td>
                        <td>
                          <div className="score-bar-wrap">
                            {c.status === 'processing' || c.status === 'new' ? (
                              <span className="score-pill badge-new" style={{ width: 'auto', padding: '3px 12px' }}>
                                <span className="sync-dot" style={{ display: 'inline-block', marginRight: '6px' }}></span>
                                Analyzing...
                              </span>
                            ) : c.status === 'error' ? (
                              <span className="score-pill score-low" style={{ width: 'auto', padding: '3px 12px', background: '#fde8e8', color: '#e74c3c' }}>
                                Error
                              </span>
                            ) : (
                              <span
                                className={`score-pill ${scoreClass(c.score)} score-pill-clickable`}
                                onClick={(e) => { e.stopPropagation(); if (c.scoring_details) setScoreModal({ name: c.name, ...c.scoring_details }); }}
                                title={c.scoring_details ? 'Click to see score breakdown' : 'No breakdown available'}
                              >{c.score.toFixed(1)}</span>
                            )}
                            <div className="score-bar-bg">
                              <div className="score-bar-fill" style={{
                                width: c.status === 'processing' || c.status === 'new' ? '100%' : `${pct}%`,
                                background: c.status === 'processing' || c.status === 'new' ? '#3498db' : barColor(c.score),
                                opacity: c.status === 'processing' || c.status === 'new' ? 0.3 : 1
                              }}></div>
                            </div>
                          </div>
                        </td>
                        <td>{renderStatusBadge(c.status)}</td>
                        <td>
                          <div className={`chevron ${isExpanded ? 'open' : ''}`}>
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="drawer-row">
                          <td colSpan={isAllJobs ? 7 : 6}>
                            <div className="drawer-inner">
                              <div className="drawer-grid">
                                <div><div className="dl">Full Name</div><div className="dv">{c.name}</div></div>
                                <div><div className="dl">Email</div><div className="dv mono">{c.email}</div></div>
                                <div><div className="dl">Phone</div><div className="dv mono">{c.phone}</div></div>
                                <div><div className="dl">Location</div><div className="dv">{c.location}</div></div>
                                <div><div className="dl">Education</div><div className="dv">{c.education || 'N/A'}</div></div>
                                <div><div className="dl">Experience</div><div className="dv">{c.experience || 'N/A'}</div></div>
                              </div>
                              <div className="drawer-actions">
                                <button
                                  className="btn btn-blue btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (c.cv_file_path) {
                                      const relativePath = c.cv_file_path.replace(/^storage\//, '');
                                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api/v1').replace('/api/v1', '');
                                      window.open(`${baseUrl}/files/${relativePath}`, '_blank');
                                    } else {
                                      alert('CV file not available for this candidate.');
                                    }
                                  }}
                                >📄 View Full CV</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* SCORE DETAILS MODAL */}
      {scoreModal && (
        <div className="cd-modal-backdrop open" onClick={() => setScoreModal(null)}>
          <div className="cd-modal score-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <div className="cd-modal-title">📊 Score Breakdown — {scoreModal.name}</div>
                <div className="cd-modal-sub">Role matched: <strong>{(scoreModal.role_used || 'general').replace(/_/g, ' ')}</strong></div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setScoreModal(null)}>✕</button>
            </div>
            <div className="score-modal-banner" style={{
              background: scoreModal.decision === 'PASS' ? 'var(--green-bg)' : scoreModal.decision === 'HOLD' ? 'var(--amber-bg)' : 'var(--red-bg)',
              border: `1px solid ${scoreModal.decision === 'PASS' ? '#a7f3d0' : scoreModal.decision === 'HOLD' ? '#fcd34d' : '#fca5a5'}`,
              borderRadius: '10px', padding: '14px 18px', marginBottom: '18px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: '4px' }}>Final Score</div>
                <div style={{ fontSize: '32px', fontWeight: 800, color: scoreModal.decision === 'PASS' ? 'var(--green-text)' : scoreModal.decision === 'HOLD' ? 'var(--amber-text)' : 'var(--red-text)' }}>
                  {scoreModal.final_score?.toFixed(2)}
                  <span style={{ fontSize: '14px', fontWeight: 500, marginLeft: '4px' }}>/10</span>
                </div>
                {scoreModal.penalty_score > 0 && (
                  <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '2px' }}>−{scoreModal.penalty_score} risk penalty applied</div>
                )}
              </div>
              <div style={{
                padding: '6px 18px', borderRadius: '99px', fontWeight: 700, fontSize: '13px',
                background: scoreModal.decision === 'PASS' ? '#00b894' : scoreModal.decision === 'HOLD' ? '#f59e0b' : '#e74c3c',
                color: '#fff'
              }}>{scoreModal.decision}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div className="cd-form-label" style={{ marginBottom: '10px' }}>Component Scores</div>
              {scoreModal.breakdown && Object.entries(scoreModal.breakdown).map(([key, val]) => {
                const weight = scoreModal.weights_used?.[`w${key.charAt(0).toUpperCase()}`];
                const componentPct = ((val / 10) * 100);
                const reasoning = scoreModal.score_reasoning?.[key];
                const labelMap = { technical: '⚙️ Technical', experience: '💼 Experience', projects: '🚀 Projects', structure: '📋 Structure', fit: '🎯 Role Fit' };
                return (
                  <div key={key} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>{labelMap[key] || key}</span>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        <strong style={{ color: 'var(--text)' }}>{val?.toFixed(1)}</strong>/10
                        {weight != null && <span style={{ marginLeft: '8px', color: 'var(--purple)', fontWeight: 600 }}>×{(weight * 100).toFixed(0)}%</span>}
                      </span>
                    </div>
                    <div style={{ height: '6px', background: 'var(--border)', borderRadius: '99px', marginBottom: '4px' }}>
                      <div style={{ height: '100%', width: `${componentPct}%`, background: 'var(--purple)', borderRadius: '99px', transition: 'width 0.5s ease' }} />
                    </div>
                    {reasoning && <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>{reasoning}</div>}
                  </div>
                );
              })}
            </div>
            {scoreModal.penalties?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div className="cd-form-label" style={{ marginBottom: '8px' }}>⚠️ Risk Penalties</div>
                {scoreModal.penalties.map((p, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--red-bg)', borderRadius: '6px', marginBottom: '5px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--red-text)', fontWeight: 500 }}>{p.risk.replace(/_/g, ' ')}</span>
                    <span style={{ fontSize: '12px', color: 'var(--red)', fontWeight: 700 }}>−{p.deduction}</span>
                  </div>
                ))}
              </div>
            )}
            {scoreModal.summary_reason && (
              <div style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: '1.55' }}>
                {scoreModal.summary_reason}
              </div>
            )}
          </div>
        </div>
      )}

      {/* DRIVE MODAL */}
      <div className={`cd-modal-backdrop ${driveModalOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setDriveModalOpen(false); }}>
        <div className="cd-modal">
          <div className="cd-modal-title">⚙️ Configure Google Drive Sync</div>
          <div className="cd-modal-sub">Connect a Drive folder so CVs are automatically pulled, scored, and shown here — no manual uploads needed.</div>
          <div style={{ marginBottom: '18px' }}>
            <div className="cd-step"><div className="cd-step-num">1</div><div className="cd-step-text">Go to <strong>Google Cloud Console</strong> → create a project → enable the <strong>Google Drive API</strong>.</div></div>
            <div className="cd-step"><div className="cd-step-num">2</div><div className="cd-step-text">Create <strong>OAuth 2.0 credentials</strong> and paste your Client ID below.</div></div>
            <div className="cd-step"><div className="cd-step-num">3</div><div className="cd-step-text">Copy the <strong>Folder ID</strong> from your Drive folder URL — the string after <code>/folders/</code>.</div></div>
            <div className="cd-step"><div className="cd-step-num">4</div><div className="cd-step-text">Set your sync interval and score threshold. CVs below the threshold are <strong>auto-hidden</strong> from the main view.</div></div>
          </div>
          <div className="cd-form-group">
            <label className="cd-form-label">Google OAuth Client ID</label>
            <input className="cd-form-input" placeholder="xxxx.apps.googleusercontent.com" />
          </div>
          <div className="cd-form-group">
            <label className="cd-form-label">Drive Folder ID</label>
            <input className="cd-form-input" placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2..." />
            <div className="cd-form-hint">drive.google.com/drive/folders/<strong>FOLDER_ID_HERE</strong></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="cd-form-group">
              <label className="cd-form-label">Sync Interval</label>
              <select style={{ minWidth: 'unset', width: '100%' }} defaultValue="Every 10 minutes">
                <option>Every 5 minutes</option>
                <option value="Every 10 minutes">Every 10 minutes</option>
                <option>Every 30 minutes</option>
                <option>Manual only</option>
              </select>
            </div>
            <div className="cd-form-group">
              <label className="cd-form-label">Score Threshold</label>
              <select style={{ minWidth: 'unset', width: '100%' }} defaultValue="7.0">
                <option>6.5</option>
                <option value="7.0">7.0</option>
                <option>7.5</option>
                <option>8.0</option>
                <option>8.5</option>
              </select>
            </div>
          </div>
          <div className="cd-modal-footer">
            <button className="btn btn-ghost" onClick={() => setDriveModalOpen(false)}>Cancel</button>
            <button className="btn btn-purple" onClick={() => { setDriveModalOpen(false); showToast("✅ Drive sync configured successfully"); }}>Save &amp; Connect</button>
          </div>
        </div>
      </div>

      {/* UPLOAD MODAL */}
      <div className={`cd-modal-backdrop ${uploadModalOpen ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) setUploadModalOpen(false); }}>
        <div className="cd-modal">
          <div className="cd-modal-title">📄 Upload CVs Manually</div>
          <div className="cd-modal-sub">Select the job position and choose the CV files to upload. Data will be automatically extracted and saved.</div>
          <div className="cd-form-group">
            <label className="cd-form-label">Job Position</label>
            <select className="cd-form-input" value={uploadRole} onChange={(e) => setUploadRole(e.target.value)}>
              <option value="">— Select Job Position —</option>
              {openJobs.length === 0 ? (
                <option value="" disabled>No open positions available</option>
              ) : (
                openJobs.map(job => (
                  <option key={job.id} value={job.roleId || job.id}>{job.title}</option>
                ))
              )}
            </select>
          </div>
          <div className="cd-form-group">
            <label className="cd-form-label">CV Files (.pdf, .doc, .docx)</label>
            <input
              key={uploadModalOpen ? "open" : "closed"}
              type="file"
              multiple
              accept=".pdf,.doc,.docx"
              className="cd-form-input"
              style={{ paddingTop: '8px' }}
              onChange={(e) => setUploadFiles(e.target.files ? Array.from(e.target.files) : [])}
            />
          </div>
          <div className="cd-modal-footer">
            <button className="btn btn-ghost" onClick={() => setUploadModalOpen(false)}>Cancel</button>
            <button
              className="btn btn-purple"
              disabled={!uploadRole || uploadFiles.length === 0 || syncing}
              onClick={handleUploadSubmit}
            >
              {syncing ? 'Uploading...' : `Upload ${uploadFiles.length} file(s)`}
            </button>
          </div>
        </div>
      </div>

      {/* TOAST */}
      <div className={`cd-toast ${toastMsg ? 'show' : ''}`}>{toastMsg}</div>
    </div>
  );
};

export default CVUploadTab;